import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import Apps from "constants/applications";
import ACCOUNT_TYPE_TO_SUBDOMAIN_MAP, { CURRENT_PORTAL } from "constants/app_subdomains";
import { NOTARY_CORPORATE_ENTRYPOINT } from "constants/notary";
import { getTierPricingUrl } from "util/tier";
import Env from "config/environment";
import { loginReset } from "redux/actions/authentication";
import AccountLocked from "assets/images/account/account-locked.svg";
import LoginIllustration from "assets/images/notarize-illustrations/login-illustration.svg";
import Button from "common/core/button";
import Link from "common/core/link";
import { EnvironmentIndicator } from "common/app_frame/header/dev";
import { SupportLink } from "common/core/mailto_link";
import { Heading } from "common/core/typography";

import GoogleSignInButton from "../google_signin_button";
import Styles from "./index.module.scss";
import type { SsoProvider } from "./proof/screens/saml";

const { hasTestingEnvironmentWarning } = Env;
const currentPortal = ACCOUNT_TYPE_TO_SUBDOMAIN_MAP[CURRENT_PORTAL];

export function Background() {
  return (
    <div className={Styles.backgroundIllustration}>
      <img
        alt="A whimsical illustration depicting notarizing a document on a computer or a phone"
        src={LoginIllustration}
      />
    </div>
  );
}

export function LoginHeader({ liveRegion }: { liveRegion?: React.AriaAttributes["aria-live"] }) {
  return (
    <div aria-live={liveRegion}>
      <Heading level="h1" textStyle="headingTwo" textAlign="center" className={Styles.mainHeading}>
        <FormattedMessage
          id="bb5bfb64-1ae4-4243-b3a8-9ac0b8e46f3f"
          defaultMessage="Welcome back!"
        />
      </Heading>
      <Heading level="h2" textStyle="headingFive" className={Styles.secondaryHeading}>
        <FormattedMessage
          id="abeac662-cfb2-4e99-b055-940cfdaa8021"
          defaultMessage="Sign into your {currentPortal, select, app{signer} other{{currentPortal}}} account"
          values={{ currentPortal }}
        />
      </Heading>
    </div>
  );
}

export function SamlLoginHeader({ ssoProvider }: { ssoProvider: SsoProvider }) {
  const logo = ssoProvider.organization.logoUrl;

  return (
    <div aria-live="polite" className={Styles.flexColumn}>
      {logo && (
        <img className={Styles.ssoLogo} src={logo} alt={`${ssoProvider.organization.name} logo`} />
      )}
      <Heading level="h1" textStyle="headingTwo" className={Styles.ssoHeading}>
        <FormattedMessage
          id="43999515-4960-4dc7-895a-0bbb9841ab3a"
          defaultMessage="Single sign-on to"
          tagName="span"
        />
        &nbsp;
        <span className={Styles.ssoCompanyName}>
          <FormattedMessage
            id="9465308a-218c-4373-ba6e-bf538044c898"
            defaultMessage={`<b>{name}</b>`}
            values={{
              b: (msg: ReactNode[]) => <strong>&nbsp;{msg}</strong>,
              name: ssoProvider.organization.name,
            }}
          />
        </span>
      </Heading>
      <Heading level="h2" textStyle="headingFive" className={Styles.ssoSecondaryHeading}>
        <FormattedMessage
          id="8d6e35d8-c10f-48d9-bd46-86229c08c3df"
          defaultMessage={`Authenticate your account by logging into {name}’s single sign-on provider`}
          values={{ name: ssoProvider.organization.name }}
        />
      </Heading>
    </div>
  );
}

export function ForgotPasswordHeader({
  liveRegion,
}: {
  liveRegion?: React.AriaAttributes["aria-live"];
}) {
  return (
    <>
      <Heading
        level="h1"
        textStyle="headingFour"
        textAlign="center"
        className={Styles.mainHeading}
        aria-live={liveRegion}
      >
        <FormattedMessage
          id="4bd5ba52-9543-400e-90ec-3d5d389d62a1"
          defaultMessage="Reset password"
        />
      </Heading>
    </>
  );
}

type ContinueButtonProps = {
  onClick?: () => void;
  disabled?: boolean;
};

export function Continue({ onClick, disabled }: ContinueButtonProps) {
  return (
    <Button
      className={Styles.marginBottom}
      onClick={onClick}
      type="submit"
      disabled={disabled}
      buttonColor="action"
      buttonSize="large"
      variant="primary"
      fullwidth
    >
      <FormattedMessage id="75cf03e0-80cc-4f7b-b287-a25f03dbbaed" defaultMessage="Continue" />
    </Button>
  );
}

export function SignUp() {
  return (
    <p className={Styles.smallText}>
      <FormattedMessage
        id="a7b0b68e-17ef-4c75-a415-807139143722"
        defaultMessage="Don't have an account? "
      />{" "}
      <Link
        className={Styles.link}
        underlined
        href={CURRENT_PORTAL === Apps.NOTARY ? NOTARY_CORPORATE_ENTRYPOINT : getTierPricingUrl()}
      >
        <FormattedMessage id="59975d66-d1af-4d39-be96-99660305569a" defaultMessage="Sign up" />
      </Link>
    </p>
  );
}

type ForgotPasswordLinkProps = {
  setForgotPasswordScreen: () => void;
};

export function ForgotPasswordLink({ setForgotPasswordScreen }: ForgotPasswordLinkProps) {
  return (
    <Button
      variant="tertiary"
      buttonColor="dark"
      className={Styles.forgotPassword}
      onClick={setForgotPasswordScreen}
    >
      <FormattedMessage
        id="082dee36-f5dc-4f1d-951d-91b562077b96"
        defaultMessage="Forgot password?"
      />
    </Button>
  );
}

type GoogleSignInButtonType = {
  onGoogleSignIn: (authCode: string) => void;
};

export function SignInWithGoogle({ onGoogleSignIn }: GoogleSignInButtonType) {
  return <GoogleSignInButton className={Styles.googleBtn} signIn={onGoogleSignIn} />;
}

type BackButtonProps = {
  className?: string;
  onBack: () => void;
  ["aria-label"]?: string;
};

export function BackButton({ className, onBack, "aria-label": ariaLabel }: BackButtonProps) {
  return (
    <Button
      buttonColor="action"
      variant="tertiary"
      className={className}
      onClick={onBack}
      aria-label={ariaLabel}
    >
      <FormattedMessage id="c57f3d08-d718-438d-92ac-aa0ac0391845" defaultMessage="Back" />
    </Button>
  );
}

type LoginAttemptsProps = {
  onResetPassword: () => void;
  onBack: () => void;
};

export function LoginAttemptsError({ onBack, onResetPassword }: LoginAttemptsProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const newSearchParamsWithoutForbidden = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("forbidden");
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <div className={Styles.loginAttempts}>
      <img src={AccountLocked} alt="Account locked" />
      <h1 className={Styles.h1}>
        <FormattedMessage
          id="ad0ea75a-3687-494f-91a2-ceee44f98448"
          defaultMessage="Check your email"
        />
      </h1>
      <p className={Styles.p}>
        <FormattedMessage
          id="e0f57ae2-9bc0-4a8d-87c1-60e7302b5e3c"
          defaultMessage="There have been too many unsuccessful login attempts. Use the password reset link in your email to unlock your account."
        />
      </p>

      <Button
        fullwidth
        buttonColor="action"
        buttonSize="large"
        variant="primary"
        onClick={onResetPassword}
      >
        <FormattedMessage id="b71df70a-f710-41a2-aec1-385fef35431d" defaultMessage="Resend Link" />
      </Button>
      <BackButton
        onBack={() => {
          newSearchParamsWithoutForbidden();
          onBack();
        }}
      />
    </div>
  );
}

export function EnvironmentWarningHeader() {
  if (!hasTestingEnvironmentWarning) {
    return null;
  }
  return (
    <header className={Styles.envHeader}>
      <EnvironmentIndicator />
      {currentPortal !== "admin" && (
        <div className={Styles.envMsg}>
          <FormattedMessage
            id="1e864c47-cb62-4aef-ad25-dd503c97505c"
            defaultMessage="This is a testing site. You will not be able to do a valid notarization or eSign transaction here. Please visit <a>this link</a> instead."
            values={{
              a: (msg: ReactNode[]) => (
                <a href={`https://${currentPortal}.notarize.com`} target="_blank" rel="noreferrer">
                  {msg}
                </a>
              ),
            }}
          />
        </div>
      )}
    </header>
  );
}

type BackToPasswordScreenProps = {
  backToPasswordScreen: () => void;
};

export function BackToPasswordScreen({ backToPasswordScreen }: BackToPasswordScreenProps) {
  return (
    <Button
      variant="tertiary"
      buttonColor="action"
      className={Styles.samlBackBtn}
      onClick={backToPasswordScreen}
    >
      <FormattedMessage
        id="e9b64870-2d20-4a3b-93d9-3174a819dbf6"
        defaultMessage="Use password instead"
      />
    </Button>
  );
}

type BackToLoginButtonProps = {
  redirect?: string;
};

export function BackToLoginButton({ redirect }: BackToLoginButtonProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const backToLogin = () => {
    dispatch(loginReset());
    navigate(redirect ? `/login?redirect=${redirect}` : "/login");
  };
  return (
    <Button buttonColor="action" variant="primary" onClick={backToLogin}>
      <FormattedMessage id="1a7c25d3-33a3-4dd4-a91f-69f425da7170" defaultMessage="Back to login" />
    </Button>
  );
}

export function BackToLoginLink({ redirect }: { redirect?: string | null }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const backToLogin = () => {
    dispatch(loginReset());
    navigate(redirect ? `/login?redirect=${redirect}` : "/login");
  };
  return (
    <Button buttonColor="action" variant="tertiary" onClick={backToLogin}>
      <FormattedMessage id="15b92c91-325b-4795-a8e5-c604d93ba2c2" defaultMessage="Back to login" />
    </Button>
  );
}

export function TokenInvalid() {
  return (
    <div className={Styles.flexColumnCentered}>
      <p className={Styles.invalidTokenError}>
        <FormattedMessage
          id="9e1fcf01-1a50-4680-b7d0-2d24fd453133"
          defaultMessage="This reset password link is no longer valid. Please contact {link} for more information."
          values={{
            link: <SupportLink />,
          }}
        />
      </p>
      <BackToLoginButton />
    </div>
  );
}

type ResetPasswordSubmitButtonProps = {
  submitting: boolean;
  invalid?: boolean;
};

export function ResetPasswordSubmitButton({ submitting, invalid }: ResetPasswordSubmitButtonProps) {
  return (
    <Button
      fullwidth
      buttonColor="action"
      buttonSize="large"
      variant="primary"
      type="submit"
      data-automation-id="set-password-button"
      disabled={invalid}
      className={Styles.marginBottom}
    >
      {submitting ? (
        <FormattedMessage
          id="fbdb639a-6bb6-4bc1-8922-176090423052"
          description="Currently saving text password reset"
          defaultMessage="Updating password..."
        />
      ) : (
        <FormattedMessage
          id="00e9b153-1db1-4d3b-913c-a3765d68f4b7"
          description="Submit password reset button"
          defaultMessage="Update password"
        />
      )}
    </Button>
  );
}

export function PasswordResetLinkSent({ onBack }: BackButtonProps) {
  return (
    <>
      <p className={Styles.pStyle}>
        <FormattedMessage
          id="a355f8c6-6897-47cd-ad09-45a65fdc6f9a"
          defaultMessage="A link has been sent to your email to reset your password."
        />
      </p>
      <BackButton onBack={onBack} />
    </>
  );
}
